import cn from "classnames";
import type { FC } from "react";
import {
	type ConversationType,
	type InboxConversationFiltersDto,
	Platform,
} from "shared";
import { Icon, IconSVG } from "../common/Icon";
import { useFeatureFlag } from "../hooks/use-app-metadata";
import "./InboxFilters.css";

interface InboxFiltersProps {
	filters: InboxConversationFiltersDto;
	onPlatformFilter: (platform: Platform | undefined) => void;
	onTypeFilter: (type: ConversationType | undefined) => void;
	onArchivedToggle: () => void;
}

export const InboxFilters: FC<InboxFiltersProps> = ({
	filters,
	onPlatformFilter,
	onTypeFilter,
	onArchivedToggle,
}) => {
	const isTikTokEnabled = useFeatureFlag("TikTokIntegration");

	const platforms = [
		{ value: undefined, label: "All" },
		{ value: Platform.FACEBOOK, label: "Facebook", icon: IconSVG.Facebook },
		{ value: Platform.INSTAGRAM, label: "Instagram", icon: IconSVG.Instagram },
		{ value: Platform.LINKEDIN, label: "LinkedIn", icon: IconSVG.LinkedIn },
		{ value: Platform.THREADS, label: "Threads", icon: IconSVG.BrandThreads },
		...(isTikTokEnabled
			? [
					{
						value: Platform.TIKTOK,
						label: "TikTok",
						icon: IconSVG.TikTok,
					},
				]
			: []),
	] as const;

	const types = [
		{ value: undefined, label: "All" },
		{ value: "comment" as ConversationType, label: "Comments" },
		{ value: "direct_message" as ConversationType, label: "Messages" },
	] as const;

	return (
		<div className="inbox-filters">
			<div className="inbox-filters-section">
				<div className="inbox-filters-platforms">
					{platforms.map((p) => (
						<button
							key={p.label}
							type="button"
							className={cn("inbox-filter-btn", {
								active: filters.platform === p.value,
							})}
							onClick={() => onPlatformFilter(p.value)}
							title={p.label}
						>
							{"icon" in p ? (
								<Icon name={p.icon} size={20} className="inbox-filter-icon" />
							) : (
								<span>All</span>
							)}
						</button>
					))}
				</div>
			</div>

			<div className="inbox-filters-row">
				<div className="inbox-filters-types">
					{types.map((t) => (
						<button
							key={t.label}
							type="button"
							className={cn("inbox-filter-pill", {
								active: filters.conversationType === t.value,
							})}
							onClick={() => onTypeFilter(t.value)}
						>
							{t.label}
						</button>
					))}
				</div>

				<button
					type="button"
					className={cn("inbox-filter-pill", {
						active: filters.archived,
					})}
					onClick={onArchivedToggle}
				>
					{filters.archived ? "Archived" : "Active"}
				</button>
			</div>
		</div>
	);
};
